import { useState } from "react";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import IconButton from "@mui/material/IconButton";
import Stack from "@mui/material/Stack";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import LoadingButton from "@mui/lab/LoadingButton";
import WestIcon from "@mui/icons-material/West";
import EastIcon from "@mui/icons-material/East";
import AppDialog from "~/shared/ui/AppDialog";
import { useNotify } from "~/shared/ui/feedback/SnackbarProvider";
import { statusTranslate, getListStatus } from "~/utils/advanceRequest";
import { updateStatus } from "~/services/antecipebackoffice/advanceRequest";
import { getApiErrorCode } from "~/utils/apiError";
import { useFeatures } from "~/hooks/useFeatures";
import {
  captureErrorMessage,
  captureMomentOf,
  isFixableCaptureError,
  PaymentConfirmDialog,
  SlaJustifyDialog,
  slaRequirementOf,
} from "~/features/feedback";
import { STAGE_BY_STATUS, STAGE_LABEL } from "~/features/packages";
import { packageContext } from "../packageContext";

// Mesma regra de hoje: a partir do status atual, só estes são alcançáveis pelas setas.
const STATUS_TO_ASSIGN = {
  waiting_generate_doctor_contract: ["waiting_supplier_company_input_vacancies_data", "waiting_generate_doctor_contract", "waiting_doctor_sign_contract"],
  waiting_doctor_sign_contract: ["waiting_supplier_company_input_vacancies_data", "waiting_generate_doctor_contract", "waiting_doctor_sign_contract", "waiting_payment_doctor"],
  waiting_payment_doctor: ["waiting_payment_doctor", "done"],
  done: ["done"],
};

const FALLBACK = "Ocorreu um erro ao atualizar status!";

// Rótulo do destino no botão da janela (c): etapa do status escolhido; "Antecipado" no done.
const targetLabelOf = (status) => {
  const label = STAGE_LABEL[STAGE_BY_STATUS[status]] ?? "Antecipado";
  return label === STAGE_LABEL.contract ? "Contrato" : label;
};

// Bloco `sla` da linha (BE-1g): breach aberto na etapa atual → a janela (c) abre antes da chamada.
const proactiveSlaOf = (row) => {
  const sla = row?.sla;
  if (!sla || !sla.breachOpen || sla.stage !== STAGE_BY_STATUS[row.status]) return null;
  return { stage: sla.stage, overMinutes: Math.max(0, Number(sla.elapsedMin) - Number(sla.targetMin)) || 0 };
};

const ChangeStatusModal = ({ open, setOpen, handleClose, advancePayment, updateData }) => {
  const notify = useNotify();
  const { features } = useFeatures();
  const list = getListStatus();
  const allowed = STATUS_TO_ASSIGN[advancePayment.status] || [advancePayment.status];
  const [index, setIndex] = useState(() => list.indexOf(advancePayment.status));
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  // Janela de captura aberta sobre o modal: { kind: "friction" | "sla", extra, requirement?, error? }.
  // `extra` = o que já foi coletado neste clique; cada janela entra no corpo uma vez só (sem loop).
  const [win, setWin] = useState(null);
  const selected = index >= 0 ? list[index] : advancePayment.status;
  const proactiveSla = features.feedbackCapture ? proactiveSlaOf(advancePayment) : null;

  const close = (e) => {
    if (saving) return;
    if (typeof handleClose === "function") handleClose(e);
    if (typeof setOpen === "function") setOpen(false);
  };

  const step = (delta) => {
    const next = index + delta;
    if (allowed.includes(list[next])) setIndex(next);
  };

  const done = async () => {
    setWin(null);
    if (typeof setOpen === "function") setOpen(false);
    notify({ severity: "success", message: "Status atualizado com sucesso!" });
    if (typeof updateData === "function") await updateData();
  };

  // 422 de captura: abre a janela pedida (uma vez por janela) ou mostra o erro onde o usuário está.
  const route = (e, extra) => {
    const message = captureErrorMessage(e, FALLBACK);
    const requirement = slaRequirementOf(e);
    if (requirement && !extra.sla) {
      setWin({ kind: "sla", extra, requirement });
      return;
    }
    const code = getApiErrorCode(e);
    const moment = captureMomentOf(e);
    const frictionAsked = code === "FRICTION_REQUIRED" || (code === "REASON_REQUIRED" && selected === "done" && (moment === "friction" || moment === null));
    if (frictionAsked && !extra.friction) {
      setWin({ kind: "friction", extra });
      return;
    }
    if (isFixableCaptureError(e) && moment === "friction" && extra.friction) {
      const { friction, ...rest } = extra;
      setWin({ kind: "friction", extra: rest, error: message });
      return;
    }
    if (isFixableCaptureError(e) && moment === "sla_breach" && extra.sla && win?.requirement) {
      const { sla, ...rest } = extra;
      setWin({ kind: "sla", extra: rest, requirement: win.requirement, error: message });
      return;
    }
    if (win) setWin((w) => (w ? { ...w, error: message } : w));
    else setError(message);
  };

  const submit = async (extra) => {
    setSaving(true);
    setError(null);
    setWin((w) => (w ? { ...w, error: null } : w));
    try {
      await updateStatus(advancePayment.id, selected, extra);
      setSaving(false);
      await done();
    } catch (e) {
      setSaving(false);
      route(e, extra);
    }
  };

  const save = () => {
    if (selected === "done" && features.feedbackCapture) setWin({ kind: "friction", extra: {} });
    else if (proactiveSla) setWin({ kind: "sla", extra: {}, requirement: proactiveSla });
    else submit({});
  };

  const confirmFriction = (friction) => {
    const extra = { ...win.extra, friction };
    if (proactiveSla && !extra.sla) setWin({ kind: "sla", extra, requirement: proactiveSla });
    else submit(extra);
  };

  const closeWin = () => {
    if (!saving) setWin(null);
  };

  const backwards = list.indexOf(selected) < list.indexOf(advancePayment.status);
  const context = packageContext(advancePayment);

  return (
    <AppDialog
      open={open}
      onClose={close}
      size="sm"
      busy={saving}
      title="Trocar status"
      context={context}
      actions={
        <>
          <Button onClick={close} disabled={saving} sx={{ color: "text.primary" }}>Cancelar</Button>
          <LoadingButton variant="contained" loading={saving} disabled={selected === advancePayment.status} onClick={save}>Atualizar Status</LoadingButton>
        </>
      }
    >
      <Stack spacing={2}>
        <Box>
          <Typography variant="caption" color="text.secondary">Status atual</Typography>
          <Typography data-testid="current-status" sx={{ fontWeight: 500 }}>{statusTranslate(advancePayment.status)}</Typography>
        </Box>
        <Box>
          <Typography variant="caption" color="text.secondary">Novo status</Typography>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, border: "1px solid", borderColor: "divider", borderRadius: 2, p: 0.75, mt: 0.5 }}>
            <Tooltip title="Status anterior"><IconButton aria-label="Status anterior" onClick={() => step(-1)}><WestIcon /></IconButton></Tooltip>
            <Typography data-testid="new-status" aria-live="polite" sx={{ flex: 1, textAlign: "center", fontWeight: 600, color: selected === advancePayment.status ? "text.secondary" : "primary.main" }}>
              {statusTranslate(selected)}
            </Typography>
            <Tooltip title="Próximo status"><IconButton aria-label="Próximo status" onClick={() => step(1)}><EastIcon /></IconButton></Tooltip>
          </Box>
          <Typography variant="caption" color="text.secondary">As setas só param nos status permitidos a partir do atual.</Typography>
        </Box>
        {error && <Alert severity="error">{error}</Alert>}
      </Stack>
      {win?.kind === "friction" && (
        <PaymentConfirmDialog row={advancePayment} context={context} busy={saving} error={win.error} onConfirm={confirmFriction} onClose={closeWin} />
      )}
      {win?.kind === "sla" && (
        <SlaJustifyDialog
          requirement={win.requirement}
          sla={advancePayment.sla?.stage === win.requirement.stage ? advancePayment.sla : null}
          context={context}
          targetLabel={targetLabelOf(selected)}
          direction={backwards ? "back" : "forward"}
          busy={saving}
          error={win.error}
          onConfirm={(sla) => submit({ ...win.extra, sla })}
          onClose={closeWin}
        />
      )}
    </AppDialog>
  );
};

export default ChangeStatusModal;
